import { writable } from 'svelte/store';

// Navigation pane state management
function createNavigationStore() {
    const { subscribe, set, update } = writable({
        isOpen: false,
        position: { x: 20, y: 100 },
        width: 280,
        structure: [],
        expandedSections: new Set(),
        currentHeadingId: null,
        filter: '',
        isDragging: false,
        isResizing: false,
        hasUserInteracted: false // Track if user has manually expanded/collapsed
    });

    return {
        subscribe,
        
        // Toggle the navigation pane
        toggle() {
            update(state => {
                const newIsOpen = !state.isOpen;
                
                // If opening and user hasn't interacted yet, expand all
                let expandedSections = state.expandedSections;
                if (newIsOpen && !state.hasUserInteracted && state.structure.length > 0) {
                    expandedSections = new Set();
                    const addAllIds = (items) => {
                        items.forEach(item => {
                            expandedSections.add(item.id);
                            if (item.children?.length) {
                                addAllIds(item.children);
                            }
                        });
                    };
                    addAllIds(state.structure);
                }
                
                const newState = { ...state, isOpen: newIsOpen, expandedSections };
                console.log('[NavigationStore] Toggling, new state:', newState.isOpen, 'expanded sections:', expandedSections.size);
                return newState;
            });
        },
        
        // Open the navigation pane
        open() {
            update(state => ({ ...state, isOpen: true }));
        },
        
        // Close the navigation pane
        close() {
            update(state => ({ ...state, isOpen: false }));
        },
        
        // Update position
        updatePosition(x, y) {
            update(state => ({ ...state, position: { x, y } }));
        },
        
        // Update width
        updateWidth(width) {
            update(state => ({ ...state, width: Math.max(200, Math.min(400, width)) }));
        },
        
        // Update document structure
        updateStructure(structure) {
            update(state => {
                // If user hasn't interacted yet and we get structure, expand all
                let expandedSections = state.expandedSections;
                if (!state.hasUserInteracted && structure.length > 0) {
                    expandedSections = new Set();
                    const addAllIds = (items) => {
                        items.forEach(item => {
                            expandedSections.add(item.id);
                            if (item.children?.length) {
                                addAllIds(item.children);
                            }
                        });
                    };
                    addAllIds(structure);
                }
                
                return { ...state, structure, expandedSections };
            });
        },
        
        // Toggle section expansion
        toggleSection(sectionId) {
            update(state => {
                const expanded = new Set(state.expandedSections);
                if (expanded.has(sectionId)) {
                    expanded.delete(sectionId);
                } else {
                    expanded.add(sectionId);
                }
                // Mark that user has interacted
                return { ...state, expandedSections: expanded, hasUserInteracted: true };
            });
        },
        
        // Expand all sections
        expandAll() {
            update(state => {
                const expanded = new Set();
                const addAllIds = (items) => {
                    items.forEach(item => {
                        expanded.add(item.id);
                        if (item.children?.length) {
                            addAllIds(item.children);
                        }
                    });
                };
                addAllIds(state.structure);
                // Mark that user has interacted
                return { ...state, expandedSections: expanded, hasUserInteracted: true };
            });
        },
        
        // Collapse all sections
        collapseAll() {
            update(state => ({ ...state, expandedSections: new Set(), hasUserInteracted: true }));
        },
        
        // Update current heading
        updateCurrentHeading(headingId) {
            update(state => ({ ...state, currentHeadingId: headingId }));
        },
        
        // Update filter
        updateFilter(filter) {
            update(state => ({ ...state, filter }));
        },
        
        // Set dragging state
        setDragging(isDragging) {
            update(state => ({ ...state, isDragging }));
        },
        
        // Set resizing state
        setResizing(isResizing) {
            update(state => ({ ...state, isResizing }));
        },
        
        // Reset to default state
        reset() {
            set({
                isOpen: false,
                position: { x: 20, y: 100 },
                width: 280,
                structure: [],
                expandedSections: new Set(),
                currentHeadingId: null,
                filter: '',
                isDragging: false,
                isResizing: false,
                hasUserInteracted: false
            });
        }
    };
}

export const navigationStore = createNavigationStore();